import { useLingui } from '@lingui/react/macro';

import { InputSelect } from '@/design_system/InputSelect/InputSelect';
import { useActionTypes } from '@/models/actionType';
import { useCreateAction, useHasActionAlreadyBeenRefused } from '@/models/article';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import useDebouncedState from '@/utils/useDebouncedState';

import { ActionDropdownItem, CustomActionDropdownItem } from './ActionDropdownItem';
import { PackActionDropdownItem } from './PackActionDropdownItem';

export const ActionTypeSearchSelect = ({ onBlur }: { onBlur: () => void }) => {
  const { t } = useLingui();
  const { article, request, view } = useArticleContext();

  const [query, debouncedQuery, setQuery] = useDebouncedState<string>('', 500);

  const {
    data: { actionTypes, packActionTypes } = { actionTypes: [], packActionTypes: [] },
    isFetching,
  } = useActionTypes(
    {
      articleId: article.id,
      query: debouncedQuery || undefined,
    },
    {
      keepPreviousData: true,
    }
  );

  const { mutate: createAction } = useCreateAction({
    articleId: article.id,
    requestId: request.id,
  });

  const hasActionAlreadyBeenRefused = useHasActionAlreadyBeenRefused(article.id);
  const handleSelectionChange = (id: string) => {
    if (id) {
      const newAction = actionTypes?.find(
        (actionTypeOrganization) => actionTypeOrganization.id === id
      );
      const newPackAction = packActionTypes?.find(
        (packActionTypeOrganization) => packActionTypeOrganization.id === id
      );

      if (newAction) {
        createAction({
          actionTypeOrganization: newAction,
        });
      } else if (newPackAction) {
        createAction({
          packActionTypeOrganization: newPackAction,
        });
      } else {
        createAction({
          description: '',
        });
      }

      setQuery('');
      onBlur();
    }
  };

  const actionTypeOptions = [...packActionTypes, ...actionTypes];

  // isStickyOption lets know InputSelect that this option should stick to the bottom of the options list
  const customOption = { id: 'custom', isStickyOption: true } as const;

  const actionTypeAndCustomOptions = view.services.actions.customEnabled
    ? [...actionTypeOptions, customOption]
    : actionTypeOptions;

  return (
    <InputSelect
      variant="add"
      aria-label={t({ id: 'action-type-search-dialog.add-action.label', message: 'Add an action' })}
      placeholder={t({
        id: 'article.form.actions.placeholder',
        message: 'Search a care and repair action...',
      })}
      isLoading={isFetching || query !== debouncedQuery}
      // eslint-disable-next-line jsx-a11y/no-autofocus
      autoFocus
      menuIsOpen
      value={null} // Allows to automatically clear the input when selecting a value
      inputValue={query}
      onInputChange={setQuery}
      filterOption={null} // Avoid react-select to filter options only based on their label
      onBlur={onBlur}
      options={actionTypeAndCustomOptions}
      getOptionValue={(actionType) =>
        !!actionType && 'id' in actionType ? actionType.id : 'custom'
      }
      formatOptionLabel={(actionType) => {
        if (actionType) {
          if ('isStickyOption' in actionType) {
            return <CustomActionDropdownItem key="custom" />;
          }
          if ('actions' in actionType) {
            return (
              <PackActionDropdownItem
                key={actionType.id}
                packAction={actionType}
                refusedByClient={hasActionAlreadyBeenRefused(actionType.id)}
              />
            );
          }

          return (
            <ActionDropdownItem
              key={actionType.id}
              action={actionType}
              refusedByClient={hasActionAlreadyBeenRefused(actionType.id)}
            />
          );
        }
      }}
      onChange={(actionType) => {
        if (actionType) {
          handleSelectionChange(actionType.id);
        }
      }}
    />
  );
};
